<template>
  <div
    class="menu-drawer critical-component-hide"
    :class="[
      `show-tier${currentTier}`,
      { [$class('loading')]: state === 'loading' },
    ]"
  >
    <div
      ref="menuDrawer"
      class="menu-drawer__navigation"
    >
      <nav class="menu-drawer__menus">
        <!-- Tier 1 -->
        <ul
          class="menu-drawer__menu menu-drawer__tier1"
          :class="{ 'is-active': getActiveState(false) }"
        >
          <li class="menu-drawer__menu-header">
            <span
              class="menu-drawer__menu-title pl-xl"
              v-html="$string('general.site_header.menu_drawer_placeholder_tier')"
            />

            <button
              class="menu-drawer__close icon-button"
              @click.prevent="closeOverlay(namespace)"
            >
              <slot name="close">
                <span
                  class="visually-hidden"
                  v-text="$string('accessibility.close')"
                />
                <icon-close />
              </slot>
            </button>
          </li>

          <li
            v-for="(tier1) in mobile"
            :ref="(args) => setItemRefs(args, { tier: 1, type: 'link' })"
            :key="tier1.handle"
            class="menu-drawer__menu-item"
          >
            <button
              v-if="tier1.links?.length"
              class="
                menu-drawer__link
                menu-drawer__button
                text-body-m
                text-heading-l-desktop
              "
              :tabindex="getActiveState(false) ? 0 : -1"
              @click="handleLinkClick(tier1)"
            >
              {{ tier1.title }}
              <icon-chevron-right />
            </button>

            <a
              v-else
              class="menu-drawer__link text-body-m text-heading-l-desktop"
              :href="tier1.url"
              :tabindex="getActiveState(false) ? 0 : -1"
            >
              <span
                v-if="tier1.icon"
                class="menu-drawer__tier-icon"
              >
                <img
                  :alt="tier1.icon.alt"
                  class="menu-drawer__link--image"
                  :class="{ 'lazyload': lazyload }"
                  decoding="async"
                  :src="tier1.icon"
                >
              </span>

              <span>
                {{ tier1.title }}
              </span>
            </a>

            <!-- Tier 2 -->
            <ul
              v-if="tier1.links?.length"
              class="menu-drawer__menu menu-drawer__tier2"
              :class="{ 'is-active': getActiveState(tier1) || getChildActiveState(tier1) }"
            >
              <li class="menu-drawer__menu-item menu-drawer__menu-header">
                <button
                  :ref="(args) => setItemRefs(args, {
                    handle: tier1.handle,
                    tier: 2,
                    type: 'back',
                  })"
                  class="
                    menu-drawer__link
                    menu-drawer__button
                    menu-drawer__back
                    text-heading-l
                  "
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  @click="handleLinkClick(false)"
                >
                  <span
                    class="visually-hidden"
                    v-text="`${$string('general.menu_drawer.back_to')} ${$string('general.menu_drawer.top_level')}`"
                  />
                  <icon-chevron-left />
                </button>

                <span class="menu-drawer__menu-title">
                  {{ tier1.title }}
                </span>

                <button
                  class="menu-drawer__close icon-button"
                  @click.prevent="closeOverlay(namespace)"
                >
                  <slot name="close">
                    <span
                      class="visually-hidden"
                      v-text="$string('accessibility.close')"
                    />
                    <icon-close />
                  </slot>
                </button>
              </li>

              <li
                v-for="(tier2) in tier1.links"
                :ref="(args) => setItemRefs(args, {
                  handle: tier1.handle,
                  tier: 2,
                  type: 'link',
                })"
                :key="tier2.handle"
                class="menu-drawer__menu-item"
              >
                <button
                  v-if="tier2.links?.length"
                  class="
                    menu-drawer__link
                    menu-drawer__button
                    text-body-m
                    text-heading-l-desktop
                  "
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  @click="handleLinkClick(tier2)"
                >
                  {{ tier2.title }}
                  <icon-chevron-right />
                </button>

                <a
                  v-else
                  class="menu-drawer__link text-body-m text-heading-l-desktop"
                  :href="tier2.url"
                  :tabindex="getActiveState(tier1) ? 0 : -1"
                  v-text="tier2.title"
                />

                <!-- Tier 3 -->
                <ul
                  v-if="tier2.links?.length"
                  class="menu-drawer__menu menu-drawer__tier3"
                  :class="{ 'is-active': getActiveState(tier2) }"
                >
                  <li class="menu-drawer__menu-header">
                    <button
                      :ref="(args) => setItemRefs(args, {
                        handle: tier2.handle,
                        tier: 3,
                        type: 'back',
                      })"
                      class="
                        menu-drawer__link
                        menu-drawer__button
                        menu-drawer__back
                        h3
                      "
                      :tabindex="getActiveState(tier2) ? 0 : -1"
                      @click="handleLinkClick(tier1)"
                    >
                      <span
                        class="visually-hidden"
                        v-text="`${$string('general.menu_drawer.back_to')} ${tier2.title}`"
                      />
                      <icon-chevron-left />
                    </button>

                    <span class="menu-drawer__menu-title">
                      {{ tier2.title }}
                    </span>

                    <button
                      class="menu-drawer__close icon-button"
                      @click.prevent="closeOverlay(namespace)"
                    >
                      <slot name="close">
                        <span
                          class="visually-hidden"
                          v-text="$string('accessibility.close')"
                        />
                        <icon-close />
                      </slot>
                    </button>
                  </li>

                  <li
                    v-for="(tier3) in tier2.links"
                    :ref="(args) => setItemRefs(args, {
                      handle: tier2.handle,
                      tier: 3,
                      type: 'link',
                    })"
                    :key="tier3.handle"
                    class="menu-drawer__menu-item"
                  >
                    <a
                      class="
                        menu-drawer__link
                        text-body-m
                        text-heading-l-desktop
                      "
                      :href="tier3.url"
                      :tabindex="getActiveState(tier2) ? 0 : -1"
                      v-text="tier3.title"
                    />
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Menu drawer
 * -----------------------------------------------------------------------------
 * Mobile menu drawer.
 * - CSS variable --menu-drawer-links-height is updated to match height of current
 *   visible links.
 *
 * @param {Array} navigation - Mobile linklist.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import IconChevronLeft from '~icons/direction/chevron/left.svg'
import IconChevronRight from '~icons/direction/chevron/right.svg'

import timings from '~/config/timings'
import IconClose from "~icons/misc/close.svg"


export default {
  name: 'MenuDrawer',

  components: {
    IconChevronLeft,
    IconChevronRight,
    IconClose,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    mobile: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      currentLink: false,
      currentHeight: false,
      itemRefs: [],
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute current item refs in current link.
     * @returns {Array}
     */
    currentItemRefs() {
      if (this.currentLink) {
        return this.itemRefs.filter((itemRef) => {
          return itemRef.handle === this.currentLink.handle
        })
      }

      return this.itemRefs.filter((itemRef) => itemRef.tier === 1)
    },

    /**
     * Compute current tier to show.
     * @returns {Number}
     */
    currentTier() {
      if (!this.currentLink) {
        return 1
      }

      return this.currentLink.tier + 1
    },
  },

  watch: {

    /**
     * Watch current link to update focus.
     */
    currentLink() {
      this.updateFocus()
      this.updateHeight()
    },

    /**
     * Watch active overlay to reset current link.
     */
    getActiveOverlay() {
      this.currentLink = false
    },

    /**
     * Watch isMobile to close on change.
     */
    isMobile() {
      if (this.isMobile) {
        return
      }

      this.closeOverlay('menuDrawer')
    },
  },

  async mounted() {
    const start = performance.now()
    this.removeStaticMenu()
    this.updateFocus()
    const end = performance.now()

    /**
     * Drawer animation must finish before calculating heights.
     * - Wait minimum amount of time based on how long querying the menu took.
     */
    const waitTime = Math.abs(timings.normal - (end - start))

    setTimeout(() => {
      this.updateHeight()
    }, waitTime)
  },

  beforeUpdate() {
    this.itemRefs = []
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      setFocusTrap: 'accessibility/setFocusTrap',
    }),

    /**
     * Get tier 1 link text or loading placeholder based on state.
     * @param {Object} tier - Tier 1 object.
     * @param {Number} index - For loop index.
     * @returns {String}
     */
    getTier1LinkText(tier, index) {
      if (this.state === 'loading') {
        const animationDelay = `animation-delay:  ${index * timings.quick / 1000}s`
        return `<span class="menu-drawer__loading" style="${animationDelay}"></span>`
      }

      return tier.title
    },

    /**
     * Get active state for nested menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getActiveState(link) {
      return this.isActive && link.handle === this.currentLink.handle
    },

    /**
     * Get active state of current nested menu's child menu.
     * @param {Object} link - Link.
     * @returns {Boolean}
     */
    getChildActiveState(link) {
      const tier3LinkActive = link.links.find((tier3Link) => {
        return tier3Link.handle === this.currentLink.handle
      })

      return Boolean(tier3LinkActive)
    },

    /**
     * Set item refs.
     * @param {HTMLElement} element - Item in v-for.
     * @param {String} data.handle - Handle of item.
     * @param {Number} data.tier - Tier of item.
     * @param {String} data.type - Type of item, `link` or `back`.
     */
    setItemRefs(element, data) {
      if (!element) {
        return
      }

      this.itemRefs.push({
        ...data,
        element,
      })
    },

    /**
     * Removes static menu drawer rendered by menu-drawer.snippet.liquid.
     * - Otherwise there would be duplicate menus.
     */
    removeStaticMenu() {
      const container = document.querySelector('[js-menu-drawer="container"]')

      if (!container) {
        return
      }

      container.remove()
    },

    /**
     * Update focus trap and scroll lock.
     */
    updateFocus() {
      nextTick(() => {
        cnvs.EventBus.emit('overlay:update', 'menuDrawer')
      })
    },

    /**
     * Update height of current active navigation.
     * - Goes through each item in currentItemRefs and get height of element.
     */
    updateHeight() {
      nextTick(() => {
        let height = 0

        this.currentItemRefs.forEach((ref) => {
          if (!ref) {
            return
          }

          height += ref.element.offsetHeight
        })

        /**
         * If height is 0 then re-run function as it's a bug.
         */
        if (height === 0 && this.currentItemRefs.length) {
          setTimeout(() => {
            this.updateHeight()
            return
          }, 100)
        }

        /**
         * Set CSS variable.
         */
          document.documentElement.style.setProperty(
            '--menu-drawer-links-height',
            `${height}px`,
          )

        /**
         * Update Vue data for easier debugging.
         */
        this.currentHeight = height
      })
    },

    /**
     * Handle link click.
     * @param {Object|Boolean} link - Clicked link object, or false.
     */
    handleLinkClick(link) {
      this.currentLink = link
    },
  },
}
</script>

<style lang="scss">
@import './menu-drawer';
</style>
